import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Check, CheckCircle } from "@phosphor-icons/react";
import {
  AssetMaintenanceSchedule,
  MaintenanceFrequencyLabels,
} from "shared/types/asset";
import { useCompleteMaintenanceEvent } from "hooks/useAssets";
import { toast } from "react-toastify";
import { differenceInDays } from "date-fns";
import { Link } from "react-router-dom";

interface OverdueMaintenanceListProps {
  schedules: AssetMaintenanceSchedule[];
  onRefetch: () => void;
}

function getCategoryColor(schedule: AssetMaintenanceSchedule): string {
  return schedule.asset?.assetCategory?.color || "#6b7280";
}

export const OverdueMaintenanceList = ({
  schedules,
  onRefetch,
}: OverdueMaintenanceListProps) => {
  const { completeEvent, isCompleting } = useCompleteMaintenanceEvent();
  const now = new Date();

  // Filter to only overdue and sort by most overdue first
  const overdueSchedules = schedules
    .filter(
      (s) => s.active && s.nextDueDate && new Date(s.nextDueDate) < now
    )
    .sort(
      (a, b) =>
        new Date(a.nextDueDate!).getTime() -
        new Date(b.nextDueDate!).getTime()
    );

  const handleComplete = async (scheduleId: number) => {
    try {
      await completeEvent(scheduleId);
      toast.success("Onderhoud gevalideerd");
      onRefetch();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon onderhoud niet valideren";
      toast.error(message);
      console.error("Error completing maintenance event:", error);
    }
  };

  if (overdueSchedules.length === 0) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <CheckCircle
          size={32}
          className="mx-auto mb-2 text-green-500"
          weight="fill"
        />
        <p className="text-sm text-muted-foreground">
          Geen achterstallige onderhoudstaken
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-red-50 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-red-800">
            Achterstallig onderhoud
          </h3>
          <Badge variant="destructive" className="text-xs">
            {overdueSchedules.length}
          </Badge>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Voorziening
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Categorie
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Gebouw
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Schema
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Verstreken
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Frequentie
              </th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">
                Leverancier
              </th>
              <th className="text-right px-4 py-2 font-medium text-muted-foreground">
                Actie
              </th>
            </tr>
          </thead>
          <tbody>
            {overdueSchedules.map((schedule) => {
              const daysOverdue = differenceInDays(
                now,
                new Date(schedule.nextDueDate!)
              );
              const categoryColor = getCategoryColor(schedule);

              return (
                <tr
                  key={schedule.id}
                  className="border-b last:border-b-0 hover:bg-muted/30"
                >
                  <td className="px-4 py-2">
                    {schedule.asset ? (
                      <Link
                        to={`/asset/${schedule.asset.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {schedule.asset.name || schedule.asset.id}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {schedule.asset?.assetCategory ? (
                      <Badge
                        variant="outline"
                        className="text-xs"
                        style={{
                          borderColor: categoryColor,
                          color: categoryColor,
                        }}
                      >
                        {schedule.asset.assetCategory.name}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {schedule.asset?.building?.id || "-"}
                  </td>
                  <td className="px-4 py-2 text-xs">{schedule.name}</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-red-600">
                      {daysOverdue} {daysOverdue === 1 ? "dag" : "dagen"} te
                      laat
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {MaintenanceFrequencyLabels[schedule.frequency]}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {schedule.supplier?.name || "-"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleComplete(schedule.id)}
                      disabled={isCompleting}
                      className="text-xs h-7"
                    >
                      <Check size={12} className="mr-1" />
                      Valideer
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
